import React, { useEffect, useState } from "react";
import { FileText, HelpCircle, Cog, SearchCheck, PackageCheck, CheckCircle2, Download, FolderLock, Mail, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { fetchWorkflow, fetchClientQueries, fetchVault, queriesPdfUrl, fetchDigest, sendDigest, getDigestSchedule, setDigestSchedule } from "../../api";

const STEP_ICONS = {
  Documents: FileText,
  Questions: HelpCircle,
  Preparation: Cog,
  Review: SearchCheck,
  Delivered: PackageCheck,
};

const DigestCard = () => {
  const [preview, setPreview] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);

  useEffect(() => {
    getDigestSchedule().then(setSchedule).catch(() => {});
  }, []);

  const toggleSchedule = async () => {
    if (schedBusy || !schedule) return;
    const enabling = !schedule.enabled;
    const email = (recipient.trim() || schedule.recipient_email || "").trim();
    if (enabling && !email) {
      toast.error("Enter a recipient email first", { description: "Type the client's email in the field below, then enable the weekly digest." });
      return;
    }
    setSchedBusy(true);
    try {
      const res = await setDigestSchedule(email, enabling);
      setSchedule({ ...schedule, enabled: res.enabled, recipient_email: res.recipient_email });
      toast.success(enabling ? "Weekly digest enabled" : "Weekly digest paused",
        { description: enabling ? `Every Friday to ${res.recipient_email}` : undefined });
    } catch (e) {
      toast.error("Could not update schedule",
        { description: (e.response && e.response.data && e.response.data.detail) || undefined });
    }
    setSchedBusy(false);
  };

  const loadPreview = async () => {
    try {
      setPreview(await fetchDigest());
    } catch {
      toast.error("Could not build the digest preview");
    }
  };

  const send = async () => {
    if (!recipient.trim() || busy) return;
    setBusy(true);
    try {
      const res = await sendDigest(recipient.trim());
      toast.success("Digest sent", { description: `Delivered to ${res.recipient} via Resend` });
    } catch (e) {
      toast.error("Send failed", {
        description: (e.response && e.response.data && e.response.data.detail) || "Email delivery failed",
      });
    }
    setBusy(false);
  };

  return (
    <div className="glass-card rounded-md border border-white/10 p-6 space-y-4" data-testid="digest-card">
      <div className="flex items-center justify-between border-b border-white/10 pb-3">
        <h2 className="text-sm font-bold text-white flex items-center">
          <Mail className="w-4 h-4 mr-2 text-blue-400" /> Email Digest
        </h2>
        <span className="text-[11px] text-zinc-400">Progress, open questions and flagged items — in one client-friendly email</span>
      </div>
      <div className="flex items-center flex-wrap gap-2">
        <button
          data-testid="digest-preview-btn"
          onClick={loadPreview}
          className="px-3 py-2 bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold rounded-md transition flex items-center space-x-1.5"
        >
          <Eye className="w-3.5 h-3.5" />
          <span>{preview ? "Refresh Preview" : "Preview Digest"}</span>
        </button>
        <input
          data-testid="digest-recipient-input"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="client@example.com"
          className="flex-1 min-w-[220px] bg-black border border-white/20 rounded-md px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500"
        />
        <button
          data-testid="digest-send-btn"
          onClick={send}
          disabled={busy || !recipient.trim()}
          className="px-4 py-2 bg-white hover:bg-zinc-200 disabled:opacity-50 text-black text-xs font-bold rounded-md transition-colors flex items-center space-x-1.5"
        >
          <Send className="w-3.5 h-3.5" />
          <span>{busy ? "Sending…" : "Send Digest"}</span>
        </button>
      </div>
      {preview && (
        <div className="rounded-md overflow-hidden border border-white/10 bg-white" data-testid="digest-preview">
          <div dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}
      {schedule && (
        <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2.5" data-testid="digest-schedule-row">
          <div className="text-[11px] text-zinc-400">
            <span className="font-semibold text-zinc-200">Weekly Auto Digest</span>
            {schedule.enabled ? (
              <span data-testid="digest-schedule-status"> — every Friday to <span className="text-emerald-400 font-mono">{schedule.recipient_email}</span>{schedule.last_sent_at ? ` · last sent ${new Date(schedule.last_sent_at).toLocaleDateString()}` : ""}</span>
            ) : (
              <span data-testid="digest-schedule-status"> — off. Uses the recipient email above.</span>
            )}
          </div>
          <button
            data-testid="digest-schedule-toggle"
            onClick={toggleSchedule}
            disabled={schedBusy}
            className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-colors disabled:opacity-50 ${
              schedule.enabled
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25"
                : "bg-zinc-900 text-zinc-300 border border-white/15 hover:bg-white/5"
            }`}
          >
            {schedBusy ? "Saving…" : schedule.enabled ? "Enabled ✓" : "Enable"}
          </button>
        </div>
      )}
      <p className="text-[10px] text-zinc-600">
        Sent from notices@realtime-pnl.com via Resend (verified domain) — deliverable to any client inbox.
      </p>
    </div>
  );
};

const ClientPortalView = ({ entity }) => {
  const [wf, setWf] = useState(null);
  const [queries, setQueries] = useState(null);
  const [vault, setVault] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchWorkflow().then(setWf).catch(() => setError(true));
    fetchClientQueries().then(setQueries).catch(() => {});
    fetchVault().then(setVault).catch(() => {});
  }, []);

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-portal">
        Unable to load your engagement status.
      </div>
    );
  if (!wf)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-portal">
        Loading your engagement…
      </div>
    );

  const cs = wf.client_status;
  const currentIdx = cs.steps.indexOf(cs.step);

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-portal">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center space-y-2 pt-6">
          <p className="text-xs uppercase tracking-widest text-blue-400 font-bold">Client Portal</p>
          <h1 className="text-3xl font-black text-white">{entity}</h1>
          <p className="text-zinc-400 text-sm">
            Tax year {2025} engagement · your accountant sees nine internal states — you see five simple steps.
          </p>
        </div>

        {/* BALL IN COURT */}
        <div
          data-testid="ball-in-court-banner"
          className={`rounded-md border p-5 text-center text-sm font-semibold ${
            cs.waiting_on_client
              ? "bg-amber-500/10 border-amber-500/40 text-amber-800"
              : cs.step === "Delivered"
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-800"
              : "bg-blue-500/10 border-blue-500/30 text-blue-200"
          }`}
        >
          {cs.ball_in_court}
        </div>

        {/* 5-STEP TRACKER */}
        <div className="glass-card rounded-md border border-white/10 p-8">
          <div className="flex items-start justify-between relative" data-testid="client-stepper">
            <div className="absolute top-5 left-10 right-10 h-0.5 bg-white/10">
              <div
                className="h-full bg-blue-500 transition-all duration-700"
                style={{ width: `${(currentIdx / (cs.steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            {cs.steps.map((step, i) => {
              const Icon = STEP_ICONS[step];
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <div key={step} className="flex flex-col items-center space-y-2 relative z-10 w-20" data-testid={`client-step-${step.toLowerCase()}`}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition ${
                      active
                        ? "bg-blue-600 border-blue-600 text-white shadow-glow-blue"
                        : done
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-zinc-900 border-white/10 text-zinc-600"
                    }`}
                  >
                    {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-[11px] font-bold ${active ? "text-blue-300" : done ? "text-emerald-400" : "text-zinc-500"}`}>
                    {step}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* OPEN QUESTIONS FOR THE CLIENT */}
        <div className="glass-card rounded-md border border-white/10 p-6 space-y-4" data-testid="client-queries-card">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <h2 className="text-sm font-bold text-white flex items-center">
              <HelpCircle className="w-4 h-4 mr-2 text-amber-400" /> Questions For You
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-black" data-testid="client-queries-count">
                {queries ? queries.count : 0}
              </span>
            </h2>
            <a
              data-testid="download-queries-btn"
              href={queriesPdfUrl()}
              download="PnLFlow_Open_Questions.pdf"
              className="px-3 py-1.5 bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-[11px] font-semibold rounded-md transition flex items-center space-x-1.5"
            >
              <Download className="w-3 h-3 text-blue-400" />
              <span>Download Question List (PDF)</span>
            </a>
          </div>
          {!queries || queries.count === 0 ? (
            <p className="text-xs text-emerald-400 flex items-center" data-testid="client-queries-empty">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> No open questions right now — nothing is needed from you.
            </p>
          ) : (
            <div className="space-y-2" data-testid="client-queries-list">
              {queries.queries.map((q) => (
                <div key={q.number} className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4 text-xs space-y-1" data-testid={`client-query-${q.number}`}>
                  <div className="font-bold text-white">
                    {q.number}. Regarding: {q.subject}
                  </div>
                  <p className="text-zinc-400 leading-relaxed">{q.question}</p>
                </div>
              ))}
              <p className="text-[11px] text-zinc-400">
                These come straight from your accountant's open review notes — answering them keeps your engagement moving.
              </p>
            </div>
          )}
        </div>

        {/* STATEMENT VAULT */}
        {vault && (
          <div className="glass-card rounded-md border border-white/10 p-6 space-y-4" data-testid="vault-card">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-sm font-bold text-white flex items-center">
                <FolderLock className="w-4 h-4 mr-2 text-blue-400" /> Documents On File
                <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 text-[10px] font-black" data-testid="vault-count">
                  {vault.count}
                </span>
              </h2>
              <span className="text-[11px] text-zinc-400">
                Each statement is fingerprinted — duplicates can never be processed twice
              </span>
            </div>
            <table className="w-full text-left text-[11px]" data-testid="vault-table">
              <thead className="text-zinc-400 uppercase tracking-wider border-b border-white/10">
                <tr>
                  <th className="py-2">Source</th>
                  <th className="py-2">Account</th>
                  <th className="py-2">Period</th>
                  <th className="py-2 text-center">Lines</th>
                  <th className="py-2 text-right">Closing Balance</th>
                  <th className="py-2 text-right">Document Fingerprint</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-zinc-300">
                {vault.statements.map((s) => (
                  <tr key={s.statement_id} data-testid={`vault-row-${s.statement_id}`}>
                    <td className="py-2 font-semibold">{s.source}</td>
                    <td className="py-2 text-zinc-400">{s.account}</td>
                    <td className="py-2 font-mono text-zinc-400">{s.period_start} → {s.period_end}</td>
                    <td className="py-2 text-center font-mono">{s.lines}</td>
                    <td className="py-2 text-right font-mono">
                      {`${s.closing_balance < 0 ? "-" : ""}$${Math.abs(s.closing_balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                    </td>
                    <td className="py-2 text-right">
                      <span className="font-mono text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-zinc-400">
                        {s.document_hash.slice(0, 16)}…
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* EMAIL DIGEST */}
        <DigestCard />

        {/* WHAT EACH STEP MEANS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          {[
            ["Documents", "We collect your bank and credit card statements. Duplicates are automatically suppressed — upload freely."],
            ["Questions", "If anything needs your confirmation (an unknown vendor, a loan split), it shows up here."],
            ["Preparation", "Every line is classified, transfers are eliminated, and twelve integrity controls run over the population."],
            ["Review", "A second set of eyes signs off. The preparer can never review their own work — the engine enforces it."],
            ["Delivered", "Your P&L, Schedule M-1 and depreciation schedule are issued by the partner as a versioned deliverable."],
          ].map(([title, desc]) => (
            <div key={title} className={`glass-card rounded-md border p-4 space-y-1 ${cs.step === title ? "border-blue-500/40 bg-blue-500/10" : "border-white/10"}`}>
              <div className="font-bold text-white">{title}</div>
              <p className="text-zinc-400 leading-relaxed">{desc}</p>
            </div>
          ))}
          <div className="glass-card rounded-md border border-white/10 p-4 space-y-1">
            <div className="font-bold text-white">Your team</div>
            <p className="text-zinc-400 leading-relaxed">
              Preparer <span className="font-mono text-zinc-300">{wf.team.preparer}</span> · Reviewer{" "}
              <span className="font-mono text-zinc-300">{wf.team.reviewer}</span> · Partner{" "}
              <span className="font-mono text-zinc-300">{wf.team.partner}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPortalView;
